import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:5001') + '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear local storage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      localStorage.removeItem('user');
      
      // Redirect based on current path
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        window.location.href = '/admin/login';
      } else if (path.startsWith('/coordinator')) {
        window.location.href = '/coordinator/login';
      } else {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // User login with group code
  userLogin: (data) => api.post('/auth/user', data),
  
  // Admin login
  adminLogin: (data) => api.post('/auth/admin', data),
  
  // Coordinator login
  coordinatorLogin: (data) => api.post('/auth/coordinator', data),
  
  // Register first admin (only works if no admins exist)
  registerAdmin: (data) => api.post('/auth/admin/register', data)
};

// Groups API
export const groupsAPI = {
  // Get all groups (admin only)
  getAllGroups: () => api.get('/groups'),
  
  // Create a group (admin only)
  createGroup: (data) => api.post('/groups', data),
  
  // Get a group by ID (admin or coordinator)
  getGroupById: (id) => api.get(`/groups/${id}`),
  
  // Update a group (admin only)
  updateGroup: (id, data) => api.put(`/groups/${id}`, data),
  
  // Delete a group (admin only)
  deleteGroup: (id) => api.delete(`/groups/${id}`),
  
  // Get campaigns for a group (admin or coordinator)
  getGroupCampaigns: (id) => api.get(`/groups/${id}/campaigns`),
  
  // Create a campaign for a group (admin only)
  createCampaign: (id, data) => api.post(`/groups/${id}/campaigns`, data),
  
  // Get a campaign by ID (admin or coordinator)
  getCampaignById: (groupId, campaignId) => api.get(`/groups/${groupId}/campaigns/${campaignId}`),
  
  // Update a campaign (admin only)
  updateCampaign: (groupId, campaignId, data) => api.put(`/groups/${groupId}/campaigns/${campaignId}`, data),
  
  // Delete a campaign (admin only)
  deleteCampaign: (groupId, campaignId) => api.delete(`/groups/${groupId}/campaigns/${campaignId}`),
  
  // Get orders for a group (admin or coordinator)
  getGroupOrders: (id, params = {}) => api.get(`/groups/${id}/orders`, { params }),
  
  // Export orders for a group to CSV (admin or coordinator)
  exportGroupOrders: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const exportUrl = `${api.defaults.baseURL}/groups/${id}/orders/export?${queryString}`;
    
    return axios({
      url: exportUrl,
      method: 'GET',
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
  },
  
  // Get statistics for a group (admin or coordinator)
  getGroupStats: (id) => api.get(`/groups/${id}/stats`),
  
  // Change coordinator password (coordinator only)
  changeCoordinatorPassword: (data) => api.put('/groups/coordinator/change-password', data)
};

// Admin API
export const adminAPI = {
  // Get all group coordinators (admin only)
  getAllCoordinators: () => api.get('/admin/coordinators'),
  
  // Create a group coordinator (admin only)
  createCoordinator: (data) => api.post('/admin/coordinators', data),
  
  // Get a group coordinator by ID (admin only)
  getCoordinatorById: (id) => api.get(`/admin/coordinators/${id}`),
  
  // Update a group coordinator (admin only)
  updateCoordinator: (id, data) => api.put(`/admin/coordinators/${id}`, data),
  
  // Delete a group coordinator (admin only)
  deleteCoordinator: (id) => api.delete(`/admin/coordinators/${id}`),
  
  // Get overall statistics (admin only)
  getStats: () => api.get('/admin/stats'),
  
  // Get all orders (admin only)
  getAllOrders: (params = {}) => api.get('/admin/orders', { params }),
  
  // Get an order by ID (admin only)
  getOrderById: (id) => api.get(`/admin/orders/${id}`),
  
  // Update an order's status (admin only)
  updateOrderStatus: (id, status) => api.put(`/admin/orders/${id}`, { status }),
  
  // Change admin password (admin only)
  changePassword: (data) => api.put('/admin/change-password', data)
};

// Shopify API
export const shopifyAPI = {
  // Create a Shopify URL with note attributes
  createShopUrl: (noteAttributes, userData) => api.post('/shopify/create-shop-url', { noteAttributes, userData }),
  
  // Get products from Shopify
  getProducts: () => api.get('/shopify/products')
};

// Session API
export const sessionAPI = {
  // Create a new shopping session
  createSession: (userData) => api.post('/session', userData),
  
  // Get session by ID
  getSession: (sessionId) => api.get(`/session/${sessionId}`)
};

export default api;
